import axios from 'axios';

// Configuração base da API
// Em produção (build), usa URL relativa para Nginx fazer proxy
// Em desenvolvimento, usa localhost direto
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1'  // URL relativa - Nginx faz proxy para backend
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1');

// Instância do axios com configurações padrão
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requests (adiciona logs)
api.interceptors.request.use(
  (config) => {
    console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor para responses (trata erros globalmente)
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error);
    
    // Trata diferentes tipos de erro
    if (error.response) {
      // Erro com resposta do servidor
      const { status, data } = error.response;
      console.error(`Server Error ${status}:`, data);
      
      // Personaliza mensagens de erro baseadas no código
      switch (status) {
        case 400:
          error.userMessage = data.error || 'Dados inválidos enviados';
          break;
        case 409:
          error.userMessage = data.error || 'Voto já registrado';
          break;
        case 429:
          error.userMessage = 'Muitas tentativas. Aguarde alguns minutos.';
          break;
        case 500:
          error.userMessage = 'Erro interno do servidor. Tente novamente.';
          break;
        default:
          error.userMessage = data.error || 'Erro desconhecido';
      }
    } else if (error.request) {
      // Erro de rede
      error.userMessage = 'Erro de conexão. Verifique sua internet.';
    } else {
      // Erro de configuração
      error.userMessage = 'Erro interno da aplicação';
    }
    
    return Promise.reject(error);
  }
);

// Serviços da API
export const enqueteAPI = {
  // Gera token de rastreamento
  async gerarToken() {
    try {
      const response = await api.get('/portal/token');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Registra voto na enquete
  async registrarVoto(dadosVoto) {
    try {
      const response = await api.post('/portal/votar', dadosVoto);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtém status da enquete
  async obterStatus() {
    try {
      const response = await api.get('/portal/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtém estatísticas públicas
  async obterEstatisticas(enqueteId = 1) {
    try {
      const response = await api.get(`/portal/estatisticas/${enqueteId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Health check da API
  async healthCheck() {
    try {
      const response = await api.get('/portal/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Utilitários para localStorage
export const storage = {
  // Salva token de rastreamento
  saveTrackingToken(token) {
    try {
      const data = {
        token,
        timestamp: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 horas
      };
      localStorage.setItem('enquete_tracking_token', JSON.stringify(data));
      console.log('💾 Token de rastreamento salvo');
    } catch (error) {
      console.error('Erro ao salvar token:', error);
    }
  },

  // Obtém token de rastreamento válido
  getTrackingToken() {
    try {
      const stored = localStorage.getItem('enquete_tracking_token');
      if (!stored) return null;

      const data = JSON.parse(stored);
      
      // Verifica se o token ainda é válido
      if (Date.now() > data.expiresAt) {
        this.clearTrackingToken();
        return null;
      }

      return data.token;
    } catch (error) {
      console.error('Erro ao recuperar token:', error);
      this.clearTrackingToken();
      return null;
    }
  },

  // Remove token de rastreamento
  clearTrackingToken() {
    try {
      localStorage.removeItem('enquete_tracking_token');
      console.log('🗑️ Token de rastreamento removido');
    } catch (error) {
      console.error('Erro ao remover token:', error);
    }
  },

  // Verifica se já votou
  hasVoted() {
    try {
      const voted = localStorage.getItem('enquete_voted');
      return voted === 'true';
    } catch (error) {
      return false;
    }
  },

  // Marca como votado
  markAsVoted() {
    try {
      localStorage.setItem('enquete_voted', 'true');
      localStorage.setItem('enquete_voted_at', Date.now().toString());
      console.log('✅ Marcado como votado');
    } catch (error) {
      console.error('Erro ao marcar como votado:', error);
    }
  }
};

// Utilitários de validação
export const validation = {
  // Valida se todos os campos obrigatórios estão preenchidos
  validateFormData(data) {
    const errors = [];

    // Campos obrigatórios
    const requiredFields = [
      'res_1_favor_contra',
      'res_2_ladario', 
      'res_4_fauna_flora',
      'res_5_novo_local',
      'res_6_expectativa',
      'res_7_faixa_etaria',
      'res_8_relacao'
    ];

    requiredFields.forEach(field => {
      if (!data[field]) {
        errors.push(`Campo ${field} é obrigatório`);
      }
    });

    // Validações de valores permitidos
    const validValues = {
      res_1_favor_contra: ['sou_a_favor', 'sou_contra', 'nao_tenho_opiniao'],
      res_2_ladario: ['sim_integrar', 'nao_focar_corumba', 'nao_sei_nao_se_aplica'],
      res_4_fauna_flora: ['sim', 'nao', 'indiferente'],
      res_5_novo_local: ['sim', 'nao', 'talvez_dependendo'],
      res_6_expectativa: ['trafego_seguranca', 'design_moderno', 'identidade_cultural', 'funcionalidades_adicionais', 'outra'],
      res_7_faixa_etaria: ['abaixo_18', '19_44', '45_64', '65_mais'],
      res_8_relacao: ['morador_corumba', 'morador_ladario', 'visitante_turista', 'trabalhador_regiao', 'outra']
    };

    // Valida se os valores estão dentro das opções permitidas
    Object.keys(validValues).forEach(field => {
      if (data[field] && !validValues[field].includes(data[field])) {
        errors.push(`Valor inválido para ${field}`);
      }
    });

    // Validações condicionais
    if (data.res_6_expectativa === 'outra' && !data.res_6_outra_texto?.trim()) {
      errors.push('Especifique sua expectativa quando selecionar "Outra"');
    }

    if (data.res_8_relacao === 'outra' && !data.res_8_outra_texto?.trim()) {
      errors.push('Especifique sua relação quando selecionar "Outra"');
    }

    // Validação de tempo mínimo
    if (!data.tempo_preenchimento_ms || data.tempo_preenchimento_ms < 5000) {
      errors.push('Tempo de preenchimento muito rápido');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Sanitiza dados do formulário
  sanitizeFormData(data) {
    const sanitized = { ...data };

    // Remove espaços extras dos campos de texto
    if (sanitized.res_3_outra_texto) {
      sanitized.res_3_outra_texto = sanitized.res_3_outra_texto.trim();
    }
    if (sanitized.res_6_outra_texto) {
      sanitized.res_6_outra_texto = sanitized.res_6_outra_texto.trim();
    }
    if (sanitized.res_8_outra_texto) {
      sanitized.res_8_outra_texto = sanitized.res_8_outra_texto.trim();
    }

    return sanitized;
  }
};

export default api;
